import { moment } from "components/tdc-core/utils/commonUtils"
import locales from "constants/locales"
import PropTypes from "prop-types"
import { compose, filter, map, path } from "ramda"
import React from "react"

//Redux
import { useSelector } from "react-redux"
import { ChinaToUSD_timestamp } from "states/ducks/landing"
import { formatLocalizedDollar } from "utils/formatter"
import { Currency, OrderInfoCreationTime, PackageDetails, SelectedCountry } from "utils/selector"
import str from "utils/strings"
import { isNotNullOrEmpty, isset } from "utils/validate"

//Utils
import { checkValidPrice } from "utils/webTrack/useTrackObj"

//Components
import {
  FairNameNotice,
  GreyShadowLine,
  GreyText,
  OrangeArrow,
  OrangeText,
  PackageDetailList,
  PackageDetailText,
  PackageDetailTitle,
  PackagePeriod,
  PackagePrice,
  PackageTitle,
  PriceNotice,
  ViewMore
} from "./package-details.styles"

const durationString = (duration) => `${duration} ${duration > 1 ? str.TXT_LANDING_Package_Period_Months : str.TXT_LANDING_Package_Period_Month}`

const PackageDetailsBlock = (props) => {
  const { showAllDetails, onMoreClick } = props

  const packageDetails = useSelector(PackageDetails)
  const userSelectedCountry = useSelector(SelectedCountry)
  const currencyFromQuery = useSelector(Currency)

  const {
    currency: currencyFromPackage,
    prices,
    duration,
    amount: packageAmount,
    price,
    onlineItemFair
  } = packageDetails

  const name = path(locales[str.locale].valuePath.packageName, packageDetails) || packageDetails.nameEN
  let description = path(locales[str.locale].valuePath.packageDescription, packageDetails)
  if (!description || description === '<p><br></p>') {
    description = packageDetails.descriptionEN
  }

  const fairName = onlineItemFair
    ? path(locales[str.locale].valuePath.fairName, onlineItemFair) || onlineItemFair.nameEN
    : null

  const currency = currencyFromPackage || currencyFromQuery

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
      <PackageTitleAndPeriod name={name} duration={duration} />
      <PriceAndNotice prices={prices} price={price} packageAmount={packageAmount} currency={currency}
                      userSelectedCountry={userSelectedCountry} fairName={fairName} />
      {showAllDetails ? <MoreDetailsView details={description} /> : <ViewMoreButton onClick={onMoreClick} />}
    </div>
  )
}

const PriceAndNotice = ({ prices, price, packageAmount, currency, userSelectedCountry, fairName }) => (
  <>
    <PriceArea prices={prices} price={price} packageAmount={packageAmount} currency={currency}
               selectedCountry={userSelectedCountry} />
    <PriceNotice>{str.TXT_LANDING_Package_Prices_Notice}</PriceNotice>
    {fairName && <FairNameNotice>{fairName}</FairNameNotice>}
  </>
)

const PackageTitleAndPeriod = ({ name, duration }) => (
  <>
    <PackageTitle>{name}</PackageTitle>
    <PackagePeriod>{durationString(duration)}</PackagePeriod>
  </>
)

const MoreDetailsView = ({ details }) => {
  return (
    <>
      <GreyShadowLine>
        <PackageDetailTitle>{str.TXT_LANDING_Package_Details}</PackageDetailTitle>
      </GreyShadowLine>
      {<PackageDetailText dangerouslySetInnerHTML={{ __html: details }} />}
      {/* <DetailsArea listItems={items} /> */}
    </>
  )
}

export const PriceArea = ({ prices = [], price, packageAmount, currency, selectedCountry }) => {
  const orderInfoCreationTime = useSelector(OrderInfoCreationTime)
  const isOldOrder = orderInfoCreationTime && moment(orderInfoCreationTime).valueOf() < ChinaToUSD_timestamp

  let priceItems = []
  if (isNotNullOrEmpty(prices)) {
    // priceItems = [...prices]
    prices.length && prices.forEach(item => {
      checkValidPrice(item) && Object.keys(item.currencies).forEach(currencyKey => {
        priceItems.push({ currency: currencyKey, amount: item.currencies[currencyKey] })
      })
    })
  } else {
    // TODO: need to bind price
    const tempAmount = isset(packageAmount) ? packageAmount : path(["amount"], price)
    const tempCurrency = isset(currency) ? currency : path(["details", "currency"], price)
    priceItems = [{ currency: tempCurrency, amount: tempAmount }]
  }

  const isSelectedCountry = isNotNullOrEmpty(selectedCountry)
  const isSelectedMainland = isNotNullOrEmpty(selectedCountry) && selectedCountry.id == "CHN"
  const isSelectedHK = isNotNullOrEmpty(selectedCountry) && selectedCountry.id == "HKG"
  const isSelectedOther = isNotNullOrEmpty(selectedCountry) && selectedCountry.id != "HKG" && selectedCountry.id != "CHN"
  const isQueryCurrency = isset(currency)

  //Shorten the price list based on country select / currency query
  const shortedPriceList = compose(
    map((priceItem) => {
      return {
        priceItemAmount: priceItem.amount,
        priceItemCurrency: priceItem.currency
      }
    }),
    filter((priceItem) => {
      //Fulfill any of those will trigger showing that amount with currency
      // const matchWithSelectedRMB = isSelectedMainland && priceItem.currency == "RMB"
      const matchWithSelectedHKD = isSelectedHK && priceItem.currency == "HKD"
      const matchWithSelectedUSD = isSelectedOther && priceItem.currency == "USD"
      // const matchWithQueryCurrency = !isSelectedCountry && isQueryCurrency && currency == priceItem.currency
      // const matchWithNoConstraint = !isSelectedCountry && !isQueryCurrency

      // RMB is not necessary & China need to check USD
      const matchWithSelectedRMB = isSelectedMainland && priceItem.currency == "USD"
      const reviseCurrency = currency == "RMB" && !isOldOrder ? "USD" : currency
      const matchWithQueryCurrency = !isSelectedCountry && isQueryCurrency && reviseCurrency == priceItem.currency
      const matchWithNoConstraint = !isSelectedCountry && !isQueryCurrency && priceItem.currency !== "RMB"

      const showPrice = matchWithSelectedRMB || matchWithSelectedHKD || matchWithSelectedUSD || matchWithQueryCurrency || matchWithNoConstraint
      return showPrice
    })
  )(priceItems)

  return (
    <PackagePrice>
      {shortedPriceList &&
        shortedPriceList.map((priceObj, index) => {
          const { priceItemAmount, priceItemCurrency } = priceObj
          return (
            <React.Fragment key={index}>
              {priceItemCurrency && isset(priceItemAmount) ? (<>
                <OrangeText>{priceItemCurrency && isset(priceItemAmount) ? formatLocalizedDollar(priceItemAmount, priceItemCurrency) : null}</OrangeText>
                <GreyText>/</GreyText>
              </>) : null}
            </React.Fragment>
          )
        })}
    </PackagePrice>
  )
}

const ViewMoreButton = ({ onClick }) => (
  <ViewMore onClick={onClick}>
    {str.TXT_LANDING_Package_View_More} <OrangeArrow />
  </ViewMore>
)

export const DetailsArea = ({ listItems }) => {
  return (
    <div>
      <ul style={{ padding: "3px 17px 16px", marginBottom: 0 }}>
        {listItems.map((item, index) => {
          return <PackageDetailList key={index}>{item}</PackageDetailList>
        })}
      </ul>
    </div>
  )
}

PackageDetailsBlock.propTypes = {
  title: PropTypes.string,
  date: PropTypes.number,
  price: PropTypes.number,
  description: PropTypes.string,
  listItems: PropTypes.array,
  onChange: PropTypes.func,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  onMoreClick: PropTypes.func
}

PackageDetailsBlock.defaultProps = {
  showAllDetails: true,
  description: "",
  onMoreClick: () => {
  }
}

export default PackageDetailsBlock
